import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchContent } from "@/lib/api";
import { FadeUp, RevealImage } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { KanthaStitch, Lotus, Fish, SunMotif } from "@/components/Motifs";

const CHAPTERS = [
  { n: "01", title: "Her Story", text: "Ipsita grew up between two classrooms: the formal one, and the courtyard where her grandmother drew alpana at dawn with rice paste. One taught her to read; the other taught her to see. Years of fashion training sharpened the hand, but the courtyard never left the eye — and eventually the two educations merged into a single practice." },
  { n: "02", title: "Roots in Bengal", text: "Bengal is not Ipsita's reference library; it is her first language. The patachitra scrolls of Medinipur, the kantha of Birbhum, the terracotta temples of Bishnupur, the jamdani looms of Phulia — these are the grammar she thinks in, long before she sketches a sleeve." },
  { n: "03", title: "The Designer", text: "As a designer, Ipsita works slowly and deliberately. Silhouettes are built around the artwork, not the reverse. Natural fibres, hand processes and small artisan clusters are non-negotiable — a collection can take a year, and she considers that a feature, not a flaw." },
  { n: "04", title: "The Folk Artist", text: "Away from the cutting table, Ipsita paints. Her works on paper, clay and cloth have been shown in group exhibitions in Kolkata and Santiniketan, and remain the quiet engine of everything the label produces. The garments are, in her words, 'paintings that agreed to be worn.'" },
];

const EXHIBITIONS = [
  { year: "2025", event: "Threads of Bengal — solo presentation, Kolkata Centre for Creativity" },
  { year: "2024", event: "Loom & Line — group textile art exhibition, Santiniketan" },
  { year: "2024", event: "LAAL — collection preview, India Art Fair parallel programme" },
  { year: "2023", event: "Contemporary Patachitra — invited artist, Birla Academy of Art & Culture" },
  { year: "2023", event: "Featured in 'New Craft Voices of India' — editorial showcase" },
];

export default function About() {
  const [c, setC] = useState({});

  useEffect(() => {
    fetchContent("/content").then(setC);
  }, []);

  return (
    <main data-testid="about-page" className="pt-24">
      {/* hero */}
      <section className="max-w-[1500px] mx-auto px-5 md:px-10 pt-10 md:pt-16 pb-20">
        <FadeUp>
          <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-6">About</p>
          <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl tracking-tight text-ink">Ipsita</h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl font-serif italic text-ink/70 leading-relaxed">
            An artist whose canvas moves between paper, textile and the human form.
          </p>
        </FadeUp>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-7">
            <RevealImage
              src={c.about_hero_image || c.intro_image || "https://images.unsplash.com/photo-1633887091273-a3bd71efddde?auto=format&fit=crop&w=1600&q=80"}
              alt="Ipsita in her Kolkata studio"
              className="aspect-[16/11]"
              cursor="VIEW"
            />
          </div>
          <div className="md:col-span-5 md:pl-6 flex flex-col justify-center">
            <FadeUp delay={0.15}>
              <Lotus className="w-9 h-9 text-alta mb-8" />
              <p className="font-serif text-2xl md:text-3xl font-light leading-snug text-walnut">
                “The garments are paintings that agreed to be worn.”
              </p>
              <p className="mt-8 font-bengali text-xl text-terracotta/70">শিল্পী • নকশিকার • গল্পকার</p>
            </FadeUp>
          </div>
        </div>
      </section>

      <Marquee items={["কথা", "Her Story", "ঐতিহ্য", "Roots in Bengal", "রঙ", "The Designer", "শিল্প", "The Folk Artist"]} className="bg-bone text-ink" />

      {/* chapters */}
      <section className="py-24 md:py-36">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10">
          {CHAPTERS.map((ch, i) => (
            <FadeUp key={ch.n} delay={0.05}>
              <div
                data-testid={`about-chapter-${ch.n}`}
                className={`grid grid-cols-1 md:grid-cols-12 gap-6 py-14 border-t border-terracotta/20 ${i % 2 === 1 ? "md:text-right" : ""}`}
              >
                <div className={`md:col-span-3 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                  <span className="font-serif text-6xl md:text-7xl font-light text-alta/70">{ch.n}</span>
                </div>
                <div className={`md:col-span-9 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                  <h2 className="font-serif text-3xl md:text-4xl font-light text-ink">{ch.title}</h2>
                  <p className={`mt-5 text-base font-light leading-relaxed text-ink/70 max-w-2xl ${i % 2 === 1 ? "md:ml-auto" : ""}`}>
                    {ch.text}
                  </p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </section>

      {/* philosophy */}
      <section className="py-28 md:py-40 bg-linen relative overflow-hidden">
        <span className="absolute top-6 right-4 font-bengali text-[18vw] leading-none text-terracotta/[0.06] select-none pointer-events-none" aria-hidden="true">ঐতিহ্য</span>
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center relative">
          <FadeUp>
            <Fish className="w-9 h-9 mx-auto mb-8 text-terracotta/60" />
            <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-8">Creative Philosophy</p>
            <blockquote className="font-serif font-light italic text-3xl sm:text-4xl lg:text-5xl leading-[1.25] text-walnut">
              “Tradition is not a room to be preserved. It is a river — it only survives by moving.”
            </blockquote>
            <KanthaStitch className="mt-12 max-w-xs mx-auto" />
          </FadeUp>
        </div>
      </section>

      {/* craft & culture */}
      <section className="py-24 md:py-36">
        <div className="max-w-[1500px] mx-auto px-5 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-5">
            <FadeUp>
              <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-6">Craft & Culture</p>
              <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight text-ink leading-[1.08]">
                An ecosystem, not a supply chain
              </h2>
              <p className="mt-8 text-base font-light leading-relaxed text-ink/70">
                The studio works on long-term relationships with kantha embroiderers in Birbhum, jamdani weavers in Phulia, patua painters in Naya and clay craftsmen in Bishnupur. Artisans are credited by name on every garment's story label — because a maker is not machinery.
              </p>
            </FadeUp>
          </div>
          <div className="md:col-span-7 grid grid-cols-2 gap-5">
            <RevealImage src="https://images.unsplash.com/photo-1566152467605-fd2f144798e5?auto=format&fit=crop&w=900&q=80" alt="Handloom weaving" className="aspect-[4/5]" cursor="VIEW" />
            <RevealImage src="https://images.unsplash.com/photo-1609881583302-61548332039c?auto=format&fit=crop&w=900&q=80" alt="Terracotta craft" className="aspect-[4/5] mt-10" cursor="VIEW" delay={0.1} />
          </div>
        </div>
      </section>

      {/* exhibitions */}
      <section className="py-24 md:py-32 bg-ink text-paper grain relative">
        <div className="relative max-w-[1100px] mx-auto px-5 md:px-10">
          <FadeUp className="mb-14">
            <p className="text-[11px] tracking-[0.3em] uppercase text-alta mb-5">Exhibitions & Features</p>
            <h2 className="font-serif font-light text-4xl sm:text-5xl tracking-tight">Shown & Spoken Of</h2>
          </FadeUp>
          <ul>
            {EXHIBITIONS.map((e, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <li data-testid={`exhibition-${i}`} className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-10 py-6 border-b border-paper/15">
                  <span className="font-serif italic text-alta text-xl w-16 shrink-0">{e.year}</span>
                  <span className="text-paper/80 font-light">{e.event}</span>
                </li>
              </FadeUp>
            ))}
          </ul>
          <FadeUp className="mt-14 flex flex-wrap gap-4">
            <Link to="/work-with-ipsita" data-testid="about-collaborate-button" className="inline-flex items-center bg-alta text-paper px-8 py-3.5 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-500 hover:bg-paper hover:text-ink">
              Collaborate
            </Link>
            <Link to="/collections" data-testid="about-collections-button" className="inline-flex items-center border border-paper/50 text-paper px-8 py-3.5 text-[11px] font-medium tracking-[0.22em] uppercase transition-all duration-500 hover:bg-paper hover:text-ink">
              View Collections
            </Link>
          </FadeUp>
        </div>
        <SunMotif className="absolute bottom-10 right-10 w-16 h-16 text-paper/15" />
      </section>
    </main>
  );
}
